import { createSlice } from "@reduxjs/toolkit";
import { ADD_CART, REMOVE_CART, RESET_CART, RESET_CART_TIMESLOTS, SET_CART, UPDATE_CART } from 'services/redux/action-types'
import { cartConstants } from "services/redux/cart/constants";
import _ from "lodash";
import { bookingOptions, isBookForMyself } from "utils/constants/booking-constants";
import { createSelector } from 'reselect'
import { dataConstants } from 'services/redux/data/constants'

const initialState = {
	[ cartConstants.BUILDING_TREE ] : undefined,
	
	[ cartConstants.BOOK_DATE ]: undefined, // Moment is not available/ready here!
	
	[ cartConstants.BOOK_FOR_OPTION ]: bookingOptions.MYSELF,
	
	[ cartConstants.CAPACITIES ]: {},
	
	// Timeslots
	[ cartConstants.TIMESLOTS_SELECTED ]: false,
	
	[ cartConstants.TIMESLOTS ]: {},
	
	[ cartConstants.TIMESLOTS_LUNCH_OPTIONS ]: {},
	[ cartConstants.TIMESLOTS_TRANSPORT_LICENSE_PLATES]: {},
	[ cartConstants.TIMESLOTS_TRANSPORT_DEFAULT_LICENSE_PLATE]: null,
	
	// Extra
	[ cartConstants.BOOKING_REJECT ]: [],
	[ cartConstants.BOOKING_EXTERNAL_PERSONS ]: {},
	[ cartConstants.BOOKING_COWORKERS ]: {},
};

const updateTimeslotsSelected = ( state ) => {
	state[ cartConstants.TIMESLOTS_SELECTED ] = !_.isEmpty( state[ cartConstants.TIMESLOTS ] );
};

const cartSlice = createSlice({
	name: 'cart',
	initialState,
	reducers: {},
	extraReducers: ( builder ) => {
		builder
			.addCase( SET_CART, ( state, action ) => {
				state[ action.key ] = action.payload;
				updateTimeslotsSelected( state );
			})
			.addCase( ADD_CART, ( state, action ) => {
				state[ action.key ] = {
					...state[ action.key ],
					...action.payload,
				};
				updateTimeslotsSelected( state );
			})
			.addCase( UPDATE_CART, ( state, action ) => {
				state[ action.key ] = {
					...state[ action.key ],
					[ action.payloadKey ]: {
						...state[ action.key ]?.[ action.payloadKey ],
						...action.payload,
					},
				};
				updateTimeslotsSelected( state );
			})
			.addCase( REMOVE_CART, ( state, action ) => {
				if( state[ action.key ] ) {
					delete state[ action.key ][ action.payload ];
				}
				updateTimeslotsSelected( state );
			})
			.addCase( RESET_CART_TIMESLOTS, ( state ) => {
				state[ cartConstants.TIMESLOTS ] = {};
				state[ cartConstants.TIMESLOTS_LUNCH_OPTIONS ] = {};
				state[ cartConstants.TIMESLOTS_TRANSPORT_LICENSE_PLATES ] = {};
				state[ cartConstants.TIMESLOTS_TRANSPORT_DEFAULT_LICENSE_PLATE ] = {};
				updateTimeslotsSelected( state );
			})
			.addCase( RESET_CART, ( state ) => {
				// Preserve the selected date when starting another booking.
				return {
					...initialState,
					[ cartConstants.BOOK_DATE ]: state[ cartConstants.BOOK_DATE ],
				};
			});
	},
});

export default cartSlice.reducer;

export const selectCartEmployees = createSelector(
	(state) => {
		return state.data[dataConstants.EMPLOYEE];
	},
	(state) => {
		return state.cart[cartConstants.BOOKING_COWORKERS];
	},
	(state) => {
		return state.cart[cartConstants.BOOKING_EXTERNAL_PERSONS];
	},
	(state) => {
		return state.cart[cartConstants.BOOK_FOR_OPTION];
	},
	( employee, coworkers, externals, bookForOption ) => {
		let result = {};
		
		// First add employee self (if required)
		if( isBookForMyself( bookForOption ) ) {
			result[ employee.guid ] = employee;
		}
		
		// Then add the rest
		result = {
			...result,
			...coworkers,
			...externals
		}
		
		return result
	}
)

export const selectCartTimeslot = createSelector(
	[
		(state) => {
			return state.cart[cartConstants.TIMESLOTS];
		},
		(state, key) => {
			return key;
		}
	],
	( timeslots, key ) => {
		return _.get( timeslots, key, {} )
	}
)

export const selectCartTimeslotsByKind = createSelector(
	[
		(state) => {
			return state.cart[cartConstants.TIMESLOTS];
		},
		(state, kind) => {
			return kind;
		}
	],
	( timeslots, kind ) => {
		return _.filter( timeslots, ( o ) => {
			return o?.kind === kind;
		} )
	}
)
